/**
 * Indonesian display names for a payment gateway / tender key.
 *
 * WHY THIS IS SHARED. The receipt and the shift summary name the same tenders,
 * and they used to do it from two different places — the receipt from a local
 * map, the shift summary from three hardcoded JSX lines. The shift summary's
 * list was missing `edc-permata` entirely, so card takings disappeared from the
 * tiles a cashier reconciles the drawer against (#1749). One map, imported by
 * both, is what keeps a tender from being named on one surface and nowhere on
 * the other.
 *
 * The keys are the backend's `payment_gateway` values (`constants/index.js`:
 * CASH / EMONEY / MIDTRANS / EDC_PERMATA) plus the `paymentMethod`
 * discriminators the POS itself sends for the two EDC tenders.
 *
 * Note that `edc-permata` is ONE gateway covering both a card swipe and a QRIS
 * scan at the same terminal, so the gateway-level label is the card wording —
 * the same text the receipt prints. `edc-card` / `edc-qris` are the finer
 * method-level keys and get told apart.
 */
export const TENDER_LABEL: Readonly<Record<string, string>> = Object.freeze({
  cash: 'Tunai',
  'e-money': 'E-Money',
  emoney: 'E-Money',
  midtrans: 'QRIS',
  qris: 'QRIS',
  'edc-permata': 'Kartu Debit/Kredit (EDC Permata)',
  'edc-card': 'Kartu Debit/Kredit (EDC Permata)',
  'edc-qris': 'QRIS (EDC Permata)',
  // The shift summary buckets rows whose gateway is NULL under this key rather
  // than dropping them, so the totals stay honest. Name it instead of showing
  // a cashier the raw word "unknown".
  unknown: 'Tanpa metode',
});

/**
 * Human label for a gateway/tender key.
 *
 * An unmapped key falls back to the raw value — never to a guess, never to an
 * empty string, because a bucket that carries money must stay attributable.
 * Returns `null` only when there is nothing at all to name.
 */
export function tenderLabel(gateway: unknown): string | null {
  const raw = gateway == null ? '' : String(gateway).trim();
  if (!raw) return null;
  return TENDER_LABEL[raw.toLowerCase()] ?? raw;
}
